import React from 'react';
import type { Invoice, InvoiceProofStatus } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useInvoiceInfo } from '@hero/hooks-shared';
import { useFeature } from '@hero/web-context';
import { UploadIcon } from '@hero/web-icons';
import { InvoiceStatusBadge } from '../Status/InvoiceStatusBadge';
import { DrawerContentCard } from '../../../app/Drawer/DrawerContentCard';
import { Price } from '../../../app/Price';
import { FileThumbnail } from '../../../app/File/shared/FileThumbnail';
import { FinanceInvoiceCTA } from '../../Financing/FinanceInvoiceCTA/FinanceInvoiceCTA';
import { ActiveFinancingApplicationBanner } from '../../Financing/ActiveFinancingApplicationBanner/ActiveFinancingApplicationBanner';

/**
 * `invoiceProof.status` is server-stamped `'approved'` on upload
 * (carrier auto-approval), `'rejected'` only when an admin pushes
 * back. We intentionally render no badge for `'approved'` so the
 * UI doesn't celebrate a state the user never reasons about; the
 * presence of the proof itself communicates "all good". Pending
 * remains visible only as a back-compat for legacy docs.
 */
const PROOF_STATUS_STYLES: Record<InvoiceProofStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: '',
  rejected: 'bg-red-100 text-red-700',
};

export interface InvoiceSummaryTabProps {
  invoice: Invoice;
  /**
   * Bilateral viewer role. Drives which CTAs appear (sellers see
   * "Upload VAT invoice" + "Finance this invoice"; buyers see a
   * read-only summary) and the wording of the proof empty state.
   */
  asRole: 'buyer' | 'seller';
  /**
   * Viewer org id — threaded into `FinanceInvoiceCTA` so it can
   * run its seller-side eligibility check inline. Optional so the
   * legacy admin surface keeps rendering.
   */
  viewerOrgId?: string;
  /**
   * Click handler for the "Upload VAT invoice" CTA. The parent
   * drawer typically opens the {@link UploadInvoiceProofDrawer}.
   */
  onUploadProof?: () => void;
  /**
   * Click handler for the "Finance this invoice" CTA. The parent
   * drawer typically navigates to the financing apply wizard with
   * the invoice pre-selected.
   */
  onApplyFinancing?: (invoice: Invoice) => void;
  /**
   * Click handler for the active financing banner. The parent
   * typically navigates to the financing history tab with the
   * application pre-expanded.
   */
  onViewFinancingApplication?: (applicationId: string) => void;
}

export function InvoiceSummaryTab({
  invoice,
  asRole,
  viewerOrgId,
  onUploadProof,
  onApplyFinancing,
  onViewFinancingApplication,
}: InvoiceSummaryTabProps) {
  const { t } = useTranslation(['m-payment', 'm-invoice', 'm-financing']);
  const {
    dueDateDisplay,
    periodFromDisplay,
    periodToDisplay,
    isSameDayInvoice,
    createdAtDisplay,
    paidAtDisplay,
    isPaid,
    canUploadInvoice,
    financing,
    financingStatus,
    canFinance,
  } = useInvoiceInfo(invoice, viewerOrgId);

  const subEditInvoice = useFeature('invoice_module', 'sub_edit_invoice');
  const subFinanceInvoice = useFeature('financing_module', 'sub_carrier_apply');

  const periodDisplay = isSameDayInvoice
    ? periodFromDisplay
    : `${periodFromDisplay} - ${periodToDisplay}`;

  const proof = invoice.invoiceProof;
  const proofImageSrc = proof?.image?.url || proof?.image?.upload;
  const proofMissingOrRejected = !proof || proof.status === 'rejected';

  const showUploadCta =
    asRole === 'seller' &&
    canUploadInvoice &&
    proofMissingOrRejected &&
    subEditInvoice.isEnabled;

  // `invoice.financing` is the single source of truth for "this
  // invoice already has a FinancingApplication". The CTA self-gates
  // on `canFinance` (which already includes `!financing`) — the
  // active-badge variant only renders when an application exists.
  const showFinancingArea =
    asRole === 'seller' && subFinanceInvoice.isEnabled && (canFinance || !!financing);

  // Original-copy chip. Skip the chip for the trivial "no paper
  // trail at all" case (settings = `['pod_image']` + status =
  // `'no_pod_required'`); surface it verbatim when either side
  // carries paper-trail intent.
  const originalCopyStatus = invoice.originalCopyStatus ?? 'no_pod_required';
  const requiresOriginalCopy =
    (invoice.originalCopySettings ?? []).includes('return_original_pod');
  const showOriginalCopyChip =
    requiresOriginalCopy || originalCopyStatus !== 'no_pod_required';
  const originalCopyTone: 'gray' | 'amber' | 'green' = (() => {
    switch (originalCopyStatus) {
      case 'received':
      case 'delivered':
        return 'green';
      case 'not_received':
      case 'under_review':
      case 'ready_to_be_sent':
      case 'sent':
        return 'amber';
      case 'no_pod_required':
      default:
        return 'gray';
    }
  })();
  const ORIGINAL_COPY_TONE_CLASS: Record<typeof originalCopyTone, string> = {
    gray: 'border-gray-200 bg-gray-50 text-gray-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  };

  return (
    <div className="flex flex-col gap-4 p-5">
      {/* Financing surface (seller-only) sits at the very top so
          unpaid receivables get the strongest visual nudge. Renders
          the active badge once `invoice.financing` exists; otherwise
          drops the compact emerald `FinanceInvoiceCTA` banner.
          Driven entirely off `invoice.financing` now — no extra
          network round-trip required. */}
      {showFinancingArea &&
        (financing ? (
          <ActiveFinancingApplicationBanner
            status={financingStatus ?? 'pending'}
            applicationId={financing._id ? String(financing._id) : undefined}
            onViewApplication={onViewFinancingApplication}
          />
        ) : (
          <FinanceInvoiceCTA
            invoice={invoice}
            viewerOrgId={viewerOrgId}
            onApply={inv => onApplyFinancing?.(inv)}
          />
        ))}

      <DrawerContentCard
        title={t('m-payment:invoice.drawer.tabs.summary')}
        list={[
          { label: t('m-payment:invoice.fields.reference'), value: invoice.ref.main },
          {
            label: t('m-payment:invoice.fields.status'),
            value: <InvoiceStatusBadge status={invoice.status} />,
          },
          { label: t('m-payment:invoice.fields.period'), value: periodDisplay },
          { label: t('m-payment:invoice.fields.due'), value: dueDateDisplay },
          {
            label: t('m-payment:invoice.fields.created_at'),
            value: createdAtDisplay,
            hidden: !createdAtDisplay,
          },
          {
            label: t('m-payment:invoice.fields.paid_at'),
            value: paidAtDisplay,
            hidden: !isPaid || !paidAtDisplay,
          },
          {
            label: t('m-invoice:original_copy.label_status', {
              defaultValue: 'Original copy',
            }),
            value: (
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${ORIGINAL_COPY_TONE_CLASS[originalCopyTone]}`}>
                {t(`m-invoice:original_copy.status_${originalCopyStatus}`, {
                  defaultValue: originalCopyStatus,
                })}
              </span>
            ),
            hidden: !showOriginalCopyChip,
          },
        ]}
      />

      <DrawerContentCard
        title={t('m-payment:invoice.fields.total_amount')}
        list={[
          {
            label: t('m-payment:invoice.fields.total_amount'),
            value: <Price amount={invoice.amount.total} currency={invoice.amount.currency} />,
          },
          {
            label: t('m-payment:invoice.fields.paid_amount'),
            value: <Price amount={invoice.amount.paid} currency={invoice.amount.currency} />,
          },
          {
            label: t('m-payment:invoice.fields.remaining'),
            value: <Price amount={invoice.amount.remaining} currency={invoice.amount.currency} />,
          },
        ]}
      />

      {/*
        VAT invoice card. Three flavours:
          1. Proof attached → show ref / status / thumbnail (read-only).
          2. Proof missing/rejected + viewer is seller → show the
             "Upload your VAT invoice" CTA at the bottom of the
             section so it sits inside the same card as the
             rest of the VAT info, not as a banner on top.
          3. Proof missing + viewer is buyer → quiet empty state.
      */}
      <div className="overflow-hidden rounded-lg border border-gray-100 bg-gray-50/40">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('m-payment:invoice.proof.section_title')}
          </span>
        </div>

        {proof ? (
          <div className="px-4 py-3 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-4">
              <span className="text-sm text-muted-foreground">
                {t('m-payment:invoice.proof.ref_number')}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {proof.refNumber ? `#${proof.refNumber}` : ''}
                </span>
                {proof.status !== 'approved' && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      PROOF_STATUS_STYLES[proof.status]
                    }`}>
                    {t(`m-payment:invoice.proof.status.${proof.status}`)}
                  </span>
                )}
              </div>
            </div>

            {proof.note && (
              <div className="flex items-start justify-between gap-4">
                <span className="text-sm text-muted-foreground">
                  {t('m-payment:invoice.proof.admin_note')}
                </span>
                <span className="text-sm font-medium text-gray-900 text-end">{proof.note}</span>
              </div>
            )}

            {proofImageSrc && (
              <div className="flex justify-center pt-2">
                <FileThumbnail src={proofImageSrc} size="xl" expandable className="rounded-lg" />
              </div>
            )}

            {/* Re-upload CTA when the existing proof was rejected.
                Subdued gray so the colorful visual focus stays
                with the green financing banner above — VAT upload
                is a housekeeping action, not the headline. */}
            {proof.status === 'rejected' && showUploadCta && onUploadProof && (
              <button
                type="button"
                onClick={onUploadProof}
                className="mt-1 self-end inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50">
                <UploadIcon className="size-4" />
                {t('m-invoice:proof.banner.cta', {
                  defaultValue: 'Upload VAT invoice',
                })}
              </button>
            )}
          </div>
        ) : showUploadCta && onUploadProof ? (
          <div className="px-4 py-4 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                <UploadIcon className="size-4" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">
                  {t('m-invoice:proof.banner.title', {
                    defaultValue: 'Upload your VAT invoice',
                  })}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed mt-0.5">
                  {t('m-invoice:proof.banner.subtitle', {
                    defaultValue:
                      'Recommended for financing — lenders prefer invoices backed by your actual VAT document.',
                  })}
                </p>
              </div>
            </div>
            {/* Gray neutral CTA — the colorful "hero" focus belongs
                to the green financing banner above; VAT upload is
                a housekeeping action. Also intentionally NOT the
                primary purple so it doesn't visually fight the
                sticky "Actions" button at the bottom. */}
            <button
              type="button"
              onClick={onUploadProof}
              className="self-stretch inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50">
              <UploadIcon className="size-4" />
              {t('m-invoice:proof.banner.cta', {
                defaultValue: 'Upload VAT invoice',
              })}
            </button>
          </div>
        ) : (
          <div className="px-4 py-4">
            <p className="text-sm text-gray-500 leading-relaxed">
              {asRole === 'buyer'
                ? t('m-invoice:proof.empty.buyer_hint', {
                    defaultValue:
                      'The seller has not attached a VAT invoice yet. It will appear here once uploaded.',
                  })
                : t('m-invoice:proof.empty.seller_hint', {
                    defaultValue:
                      'No VAT invoice attached yet.',
                  })}
            </p>
          </div>
        )}
      </div>

    </div>
  );
}
